import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import {
  Users, Sparkles, ClipboardCheck, Images, Loader2, RefreshCw,
  ThumbsUp, ThumbsDown, Palette, Layers, Anchor, Sun,
} from "lucide-react";

/**
 * Sprint 5 Vision AI — Admin stats dashboard tab.
 * Read-only aggregate view of Sprint 1-4 signals across all users.
 */

function StatCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <div
      className="rounded-lg border border-white/10 bg-zinc-950/50 p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold">
        {Icon && <Icon size={12} strokeWidth={1.5} className="text-orange-400" />}
        {label}
      </div>
      <div className="mt-2 font-outfit font-black text-3xl text-white tracking-tight">
        {value ?? "—"}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function TopList({ title, icon: Icon, items, valueKey = "value", accent = "orange", testId }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...(items || []).map((i) => i.count || 0));
  return (
    <div className="rounded-lg border border-white/10 bg-zinc-950/50 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-3">
        {Icon && <Icon size={12} strokeWidth={1.5} className={`text-${accent}-400`} />}
        {title}
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-zinc-500 py-4 text-center">
          {t("vision_ai.admin_no_data")}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 8).map((it, i) => (
            <li key={it.code || it[valueKey] || it.label || `item-${i}`} className="text-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-zinc-200 truncate">
                  {it.label || it[valueKey] || it.code}
                </span>
                <span className="tabular-nums text-zinc-400 font-mono text-xs">{it.count}</span>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-${accent}-500 transition-all`}
                  style={{ width: `${(100 * (it.count || 0)) / max}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminVisionAiStats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vision/admin/stats");
      setStats(r.data);
    } catch (e) {
      // silent — parent tab still renders
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-16 text-zinc-500 gap-2">
        <Loader2 size={16} className="animate-spin" />
        {t("common.loading")}
      </div>
    );
  }
  if (!stats) return null;

  const t2 = stats.totals || {};
  const axes = stats.top_profile_axes || {};

  return (
    <div className="space-y-6" data-testid="admin-vision-ai-stats">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-outfit font-black tracking-tight text-white">
            {t("vision_ai.admin_title")}
          </h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            {t("vision_ai.admin_subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.15em] text-zinc-300 border border-white/10 rounded-lg hover:bg-white/5 transition"
          data-testid="admin-vision-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          {t("common.refresh")}
        </button>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label={t("vision_ai.admin_stat_completion")}
          value={`${t2.completion_pct || 0}%`}
          sub={t("vision_ai.admin_stat_completion_sub", {
            done: t2.users_complete_profile || 0, total: t2.eligible_users || 0,
          })}
          icon={Sparkles}
          testId="admin-vision-stat-completion"
        />
        <StatCard
          label={t("vision_ai.admin_stat_partial")}
          value={t2.users_with_any_profile || 0}
          sub={t("vision_ai.admin_stat_partial_sub")}
          icon={Users}
          testId="admin-vision-stat-partial"
        />
        <StatCard
          label={t("vision_ai.admin_stat_reviews")}
          value={t2.feedback_reviews || 0}
          sub={t("vision_ai.admin_stat_reviews_sub", {
            sig: t2.signature_ready_reviews || 0,
          })}
          icon={ClipboardCheck}
          testId="admin-vision-stat-reviews"
        />
        <StatCard
          label={t("vision_ai.admin_stat_comparisons")}
          value={t2.comparisons || 0}
          sub={t("vision_ai.admin_stat_comparisons_sub", {
            equiv: t2.comparisons_marked_equivalent || 0,
          })}
          icon={Images}
          testId="admin-vision-stat-comparisons"
        />
      </div>

      {/* Profile axes */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TopList
          title={t("vision_ai.admin_top_style")}
          items={axes.preferred_style}
          icon={Palette}
          accent="orange"
          testId="admin-vision-top-style"
        />
        <TopList
          title={t("vision_ai.admin_top_density")}
          items={axes.ornament_density}
          icon={Layers}
          accent="orange"
          testId="admin-vision-top-density"
        />
        <TopList
          title={t("vision_ai.admin_top_fidelity")}
          items={axes.source_fidelity}
          icon={Anchor}
          accent="orange"
          testId="admin-vision-top-fidelity"
        />
        <TopList
          title={t("vision_ai.admin_top_audience")}
          items={axes.target_audience}
          icon={Users}
          accent="orange"
          testId="admin-vision-top-audience"
        />
        <TopList
          title={t("vision_ai.admin_top_mood")}
          items={axes.mood_light}
          icon={Sun}
          accent="orange"
          testId="admin-vision-top-mood"
        />
      </div>

      {/* Feedback signals */}
      <div className="grid md:grid-cols-2 gap-4">
        <TopList
          title={t("vision_ai.admin_top_positives")}
          items={stats.top_positive_codes}
          icon={ThumbsUp}
          accent="emerald"
          testId="admin-vision-top-positives"
        />
        <TopList
          title={t("vision_ai.admin_top_negatives")}
          items={stats.top_negative_codes}
          icon={ThumbsDown}
          accent="rose"
          testId="admin-vision-top-negatives"
        />
      </div>

      {/* Brief tag word-clouds */}
      <div className="grid md:grid-cols-3 gap-4">
        <TopList
          title={t("vision_ai.admin_top_brief_keep")}
          items={stats.top_brief_tags?.must_keep}
          accent="emerald"
          testId="admin-vision-top-brief-keep"
        />
        <TopList
          title={t("vision_ai.admin_top_brief_remove")}
          items={stats.top_brief_tags?.must_remove}
          accent="rose"
          testId="admin-vision-top-brief-remove"
        />
        <TopList
          title={t("vision_ai.admin_top_brief_mood")}
          items={stats.top_brief_tags?.mood}
          accent="violet"
          testId="admin-vision-top-brief-mood"
        />
      </div>

      <div className="text-[10px] text-zinc-600 text-right">
        {t("vision_ai.admin_generated_at", {
          ts: stats.generated_at ? new Date(stats.generated_at).toLocaleString() : "—",
        })}
      </div>
    </div>
  );
}
